import React, { useState } from 'react';
import { gelMarkExecutionReady } from '../../services/api';
import GateChecks from './GateChecks';

/**
 * Going live is a call, not a choice.
 *
 * <h3>What this replaces</h3>
 *
 * The step used to offer "Activate it now" and record "Every call it makes now
 * passes the enforcement gate" without making a single request. The agent was
 * never marked execution ready, so it did not serve protected traffic and did
 * not appear in the Run Center — while the trail said it was live. A node that
 * claims work nobody did is the one thing this surface cannot do.
 *
 * <h3>Why the gate is shown first</h3>
 *
 * `markExecutionReady` validates the same checklist the readiness endpoint
 * returns, so an unmet requirement is a refusal the person could have seen
 * coming. Showing it turns a failure into a list of things to go and do.
 *
 * <h3>Why a refusal does not advance the step</h3>
 *
 * The spine records what happened. If the platform refused, nothing happened,
 * and the question is still open.
 */
export default function ActivateAsk({
  registrationId, agentId, archetype, onAnswer, busy = false,
}) {
  const [working, setWorking] = useState(false);
  const [problem, setProblem] = useState(null);

  const activate = async () => {
    setWorking(true);
    setProblem(null);
    try {
      const res = await gelMarkExecutionReady(registrationId);
      if (res && res.ok === false) {
        /* The gate's own words. A sentence of our own here would be right only
           for the refusal we happened to imagine. */
        setProblem(res.error || res.message || 'The platform refused to mark it execution ready.');
        return;
      }
      onAnswer({ activated: true });
    } catch (e) {
      setProblem(e?.message || 'Could not mark it execution ready.');
    } finally {
      setWorking(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--p7-space-4)' }}>
      <GateChecks archetype={archetype} registrationId={registrationId} agentId={agentId} />

      {problem ? (
        <p className="p7-note p7-note--deny" style={{ margin: 0 }}>{problem}</p>
      ) : null}

      <div style={{ display: 'flex', gap: '9px', alignItems: 'center', flexWrap: 'wrap' }}>
        <button type="button" className="p7-btn p7-btn--primary"
                disabled={busy || working || !registrationId}
                onClick={activate}>
          {working ? 'Putting it to work…' : 'Put it to work'}
        </button>
        <button type="button" className="p7-btn p7-btn--sm" disabled={busy || working}
                onClick={() => onAnswer({ activated: false })}>
          Leave it registered for now
        </button>
      </div>
    </div>
  );
}
